import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Loading } from '../components/ui/loading';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Sparkles, Search, Shuffle, CheckCircle2, Circle, Star, X, AlertTriangle } from 'lucide-react';
import { WordFilterPopover, type WordFilters } from '../components/WordFilterPopover';
import { cn } from '../lib/utils';
import { userWordsService } from '../services/userWordsService';
import { generateQuiz } from '../services/quizService';
import { useWordSelection } from '../hooks/useWordSelection';
import { ActiveFilterPills, type FilterPill } from '../components/ActiveFilterPills';
import type { Word } from '../types/word.types';

function FillInTheBlankSelectPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [allWords, setAllWords] = useState<Word[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<WordFilters>({
    confidenceLevels: [],
    sortBy: 'newest'
  });

  // Selection limit dialog state
  const [showLimitDialog, setShowLimitDialog] = useState(false);

  // Quiz generation loading state
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);

  const wordSelection = useWordSelection(allWords);

  // Render stars for confidence level
  const renderStars = (confidence: number = 0) => {
    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }, (_, index) => (
          <Star
            key={index}
            className={cn(
              "h-3 w-3",
              index < confidence ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"
            )}
          />
        ))}
      </div>
    );
  };

  // Load all user words on mount
  useEffect(() => {
    const loadWords = async () => {
      try {
        const words = await userWordsService.getUserWords();
        setAllWords(words);
      } catch (err) {
        console.error('Error loading words:', err);
        setAllWords([]);
      } finally {
        setLoading(false);
      }
    };

    loadWords();
  }, []);

  // Pre-select words from URL params after words are loaded
  useEffect(() => {
    if (!loading && allWords.length > 0 && !initialSelectionDone) {
      const wordStrings = searchParams.get('w')?.split(',') || [];
      if (wordStrings.length > 0) {
        wordStrings.forEach(wordString => {
          const decodedWord = decodeURIComponent(wordString);
          const word = allWords.find(w => w.word.toLowerCase() === decodedWord.toLowerCase());
          if (word) {
            wordSelection.toggleWordSelection(word.word);
          }
        });
        setInitialSelectionDone(true);
      }
    }
  }, [loading, allWords, searchParams, initialSelectionDone, wordSelection]); // Removed wordSelection from deps // Removed wordSelection from dependencies


  // Filter and sort words based on search query, confidence levels, and sort order
  const filteredWords = useMemo(() => {
    const filtered = allWords.filter(word => {
      // Search filter
      const matchesSearch = !searchQuery ||
        word.word.toLowerCase().includes(searchQuery.toLowerCase()) ||
        word.definitions.some(def =>
          def.meaning.toLowerCase().includes(searchQuery.toLowerCase()) ||
          def.partOfSpeech.toLowerCase().includes(searchQuery.toLowerCase())
        );

      // Confidence filter
      const matchesConfidence = filters.confidenceLevels.length === 0 ||
        filters.confidenceLevels.length === 5 || // Show all words when all levels selected
        (word.rating && filters.confidenceLevels.includes(word.rating));

      return matchesSearch && matchesConfidence;
    });

    // Sort filtered words
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'oldest':
          return new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime();
        case 'alphabetical':
          return a.word.localeCompare(b.word);
        case 'newest':
        default:
          return new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime();
      }
    });

    return filtered;
  }, [allWords, searchQuery, filters]);


  // Quick action functions
  const selectRandom = (count: number) => {
    wordSelection.randomizeSelection(count);
  };

  const clearSelection = () => {
    wordSelection.clearSelection();
  };

  // Helper function to get active filter pills
  const getActiveFilterPills = (): FilterPill[] => {
    const pills: FilterPill[] = [];

    // Confidence level filters
    [...filters.confidenceLevels].sort((a, b) => a - b).forEach(level => {
      pills.push({
        key: `confidence-${level}`,
        label: `${level}★`,
        remove: () => setFilters(prev => ({
          ...prev,
          confidenceLevels: prev.confidenceLevels.filter(l => l !== level)
        }))
      });
    });

    // Sort by filter
    if (filters.sortBy !== 'newest') {
      const sortLabels = {
        oldest: 'Oldest First',
        alphabetical: 'Alphabetical'
      };
      pills.push({
        key: 'sortBy',
        label: `Sort: ${sortLabels[filters.sortBy]}`,
        remove: () => setFilters(prev => ({ ...prev, sortBy: 'newest' }))
      });
    }

    return pills;
  };

  const handleStartGame = async () => {
    const selectedWords = wordSelection.getSelectedWords();
    if (selectedWords.length >= 2) {
      setIsGeneratingQuiz(true);
      try {
        const response = await generateQuiz(selectedWords.map(word => word.word));
        if (response.ok && response.data?.id) {
          navigate(`/practice/fill-in-the-blank/${response.data.id}`);
        } else {
          console.error('Failed to generate quiz');
          // TODO: Show error message to user
        }
      } catch (error) {
        console.error('Error starting quiz:', error);
        // TODO: Show error message to user
      } finally {
        setIsGeneratingQuiz(false);
      }
    }
  };

  const selectedWords = wordSelection.getSelectedWords();


  if (loading) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-8">
        <Loading size="lg" message="Loading your words..." />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      {/* Selection Limit Dialog */}
      <Dialog open={showLimitDialog} onOpenChange={setShowLimitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Selection Limit Reached
            </DialogTitle>
            <DialogDescription>
              You can select a maximum of 5 words for practice. Please deselect a word first to choose a different one.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowLimitDialog(false)}>
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Select Words to Practice
        </h1>
        <p className="mt-2 text-muted-foreground">
          Choose at least 2 words to create your fill-in-the-blank practice session
        </p>
      </div>

      {/* Selection Summary Card */}
      <Card className="mb-6 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <div>
              <p className="text-3xl font-bold text-foreground">
                {selectedWords.length}
                <span className="ml-2 text-xl font-normal text-muted-foreground">
                  / {Math.min(allWords.length, 5)} words
                </span>
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {selectedWords.length < 2
                  ? `Select ${2 - selectedWords.length} more word${2 - selectedWords.length === 1 ? '' : 's'} to start`
                  : "Ready to practice!"}
              </p>
            </div>
            <Button
              size="lg"
              onClick={handleStartGame}
              disabled={isGeneratingQuiz || selectedWords.length < 2}
              className="gap-2 px-8"
            >
              {isGeneratingQuiz ? 'Generating Quiz...' :
               selectedWords.length < 2 ? 'Select words to start' : 'Start Practice'}
              {isGeneratingQuiz ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>


      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search words..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filter Popover */}
        <WordFilterPopover filters={filters} onFiltersChange={setFilters} />

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => selectRandom(5)}
            className="gap-1.5"
          >
            <Shuffle className="h-4 w-4" />
            Random 5
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSelection}
            disabled={selectedWords.length === 0}
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Active Filters */}
      {getActiveFilterPills().length > 0 && (
        <ActiveFilterPills pills={getActiveFilterPills()} />
      )}

      {/* Words Grid */}
      <div className="grid gap-3 sm:grid-cols-2">
        {filteredWords.map((word) => {
          const isSelected = selectedWords.some(selected => selected.id === word.id)
          return (
            <button
              key={word.id}
              onClick={() => {
                if (!isSelected && selectedWords.length >= 5) {
                  // Show dialog when trying to select more than 5 words
                  setShowLimitDialog(true);
                } else {
                  wordSelection.toggleWordSelection(word.word);
                }
              }}
              className={cn(
                "group relative flex items-start gap-4 rounded-xl border p-4 text-left transition-all",
                isSelected
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                  : "border-border bg-card hover:border-primary/30 hover:bg-muted/50"
              )}
            >
              {/* Checkbox */}
              <div className="flex-shrink-0 pt-0.5">
                {isSelected ? (
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground/40 transition-colors group-hover:text-muted-foreground" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-foreground">{word.word}</h3>
                  {renderStars(word.rating)}
                </div>
                <p className="mt-1 text-sm text-muted-foreground line-clamp-1">
                  {word.definitions[0]?.meaning || ''}
                </p>
              </div>

              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute right-3 top-3">
                  <Badge className="bg-primary text-primary-foreground text-xs">
                    Selected
                  </Badge>
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Empty State */}
      {filteredWords.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <Search className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-foreground">No words found</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Try adjusting your search or filters
          </p>
          <Button
            variant="outline"
            className="mt-4 bg-transparent"
            onClick={() => {
              setSearchQuery("")
              setFilters({
                confidenceLevels: [],
                sortBy: 'newest'
              })
            }}
          >
            Reset Filters
          </Button>
        </div>
      )}
    </main>
  );
}

export default FillInTheBlankSelectPage;